import { Children, cloneElement, isValidElement, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import clsx from "clsx";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/solid";

export default function Carousel({
  isPreviewHorizontal,
  title,
  children,
}: {
  isPreviewHorizontal?: boolean;
  title?: React.ReactNode;
  children: React.ReactNode & { caption?: string };
}) {
  const [items, setItems] = useState<(React.ReactNode & { itemIndex: number })[]>();
  const [currentlyViewingIndex, setCurrentlyViewingIndex] = useState<number>(0);

  useEffect(() => {
    // @ts-ignore
    const carouselItems = Children.toArray(children).map((ele, index) => ({ ...ele, itemIndex: index }));
    setItems(carouselItems);
  }, []);

  if (!items) {
    return null;
  }

  return (
    <AnimatePresence>
      <div
        className={clsx(
          isPreviewHorizontal ? "relative flex flex-col items-stretch gap-6" : "grid gap-12",
          "mb-12 mr-24 mt-6 h-[90vh] grid-cols-5 items-stretch",
        )}
      >
        <button
          type="button"
          onClick={() => setCurrentlyViewingIndex((currentlyViewingIndex + 1) % items.length)}
          aria-label="next"
          className=" absolute left-0 top-[33%] scale-50 transition-transform hover:scale-75 hover:shadow-modal"
        >
          <ChevronLeftIcon className="h-20 w-20 rounded-full bg-[#1f2732] p-4 text-white" />
        </button>
        <button
          type="button"
          onClick={() => setCurrentlyViewingIndex((currentlyViewingIndex + 1) % items.length)}
          aria-label="next"
          className=" absolute right-0 top-[33%] scale-50 transition-transform hover:scale-75"
        >
          <ChevronRightIcon className="h-20 w-20 rounded-full bg-[#1f2732] p-4 text-white" />
        </button>
        {title}
        <motion.div
          key="carousel-container"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="col-span-3 mx-auto inline-block h-[60%] w-[90%] overflow-hidden rounded-3xl px-12 pb-4 pt-2 shadow-modal"
        >
          {items[currentlyViewingIndex]}
        </motion.div>
        <div className="secondary-scrollbar col-span-2 flex flex-grow overflow-scroll pr-8 [&>*:first-child]:border-accent [&>*:first-child]:shadow-[0px_0px_12px_-4px_rgba(255,200,18,1)]">
          {[
            ...items.slice(currentlyViewingIndex % items.length),
            ...items.slice(0, currentlyViewingIndex % items.length),
          ].map((ele) => (
            <motion.div
              layout
              key={`thumbnail-${ele.itemIndex}`}
              className="mx-3 mb-3 w-2/5 shrink-0 cursor-pointer snap-y snap-mandatory snap-center rounded-xl border-2 border-slate-800 p-0.5 px-4 hover:border-white hover:shadow-modal"
              onClick={() => setCurrentlyViewingIndex(ele.itemIndex)}
            >
              {isValidElement(ele) && cloneElement(ele, { isPreview: true })}
            </motion.div>
          ))}
        </div>
      </div>
    </AnimatePresence>
  );
}
